import React, { useState } from 'react';
import styled from '@emotion/styled';
import { theme } from './layout/GlobalStyles';
import { Title } from './Title';
import { StaticImage } from 'gatsby-plugin-image';
import { Icon } from './Icon';
import { Section } from './Section';

const StyledOurTeam = styled.div`
    padding: 64px 0px 64px 0px;
    background: ${({ theme }) => theme.colors.gray2};
    .selector-container {
        display: flex;
        overflow: scroll;
        padding: 0 3px;
        gap: 24px;
        padding: 0 16px;
        &::-webkit-scrollbar {
            display: none;
        }
        > div {
            flex-shrink: 0;
        }
    }
    @media ${theme.device.tablet} {
        padding: 64px 0px 128px 16px;
    }
    @media ${theme.device.laptop} {
        padding: 64px;
        margin: 0 auto;
        .outter-container {
            display: flex;
            gap: 28px;
            align-items: center;
            margin: 0 auto;
            max-width: 1400px;
        }
        .selector-container {
            flex-direction: column;
            overflow: show;
            flex-shrink: 0;
            padding: 0;
            gap: 32px;
            padding: 12px 9px 0 3px;
        }
    }
`;

const DoctorSelector = styled.div<{ index: number; i: number }>`
    padding: 35px 0;
    margin-bottom: 42px;
    position: relative;
    > div {
        position: relative;
        z-index: 1;
        transition: all 0.2s ease-in-out;
        outline: ${({ index, i }) => (index === i ? '3px fill #E4C96F' : 'none')};
        border-radius: 76px;
        -moz-outline-radius: 76px;
        -webkit-border-radius: 76px;
        width: 150px;
        height: 180px;
        img {
            border-radius: 76px;
        }
    }
    .hide {
        opacity: 1;
        transform: translateY(10%);
    }
    .appear {
        transform: translateY(-40%);
        opacity: 0;
    }
    svg {
        left: calc(50% - 23px / 2);
        position: absolute;
        bottom: 20px;
        z-index: 0;
        width: 23px;
        transition: all 0.3s ease-in-out;
    }
    @media ${theme.device.laptop} {
        margin-bottom: 0;
        padding: 2px;
        > div {
            cursor: pointer;
            transition: all 0.2s ease-in-out;
            &:hover {
                outline: 2px solid var(--dark-mode-yellow-1, #a72a2a);
                transform: translateX(6px);
            }
        }
        svg {
            display: none;
        }
    }
`;

const FeaturedDoc = styled.div`
    padding: 0 16px;
    display: flex;
    flex-direction: column;
    align-items: center;
    > div:not(.card) {
        height: 333.667px;
        padding: 32px;

        img {
            height: 333px;
        }
    }
    .card {
        position: relative;
        bottom: 100px;
        margin: 0 12px -100px 12px;
        padding: 32px;
        border-radius: 4px;
        background: ${({ theme }) => theme.colors.gold1};
        backdrop-filter: blur(4px);
        h2 {
            color: ${({ theme }) => theme.colors.red1};
            font-size: 18px;
            font-style: normal;
            font-weight: 400;
            line-height: normal;
            margin-bottom: 16px;
        }
        h2 + p {
            color: ${({ theme }) => theme.colors.lightmodebg};
            font-size: 1rem;
            font-weight: 500;
            line-height: 162%; /* 25.92px */
            letter-spacing: 1.92px;
            text-transform: uppercase;
            background: ${theme.colors.gray2};
            padding: 6px 16px;
            margin-bottom: 32px;
        }
        p {
            font-family: 'poppins';
            font-size: 18px;
            font-style: normal;
            font-weight: 400;
            color: ${theme.colors.gray2};
            line-height: normal;
        }
    }
    @media ${theme.device.tablet} {
        //main image
        flex-direction: row;
        position: relative;
        align-items: flex-start;
        //make height adjust to content but also the card which is absolute
        .card {
            flex-shrink: 0;
            width: 50%;
            position: relative;
            top: 80px;
            right: 132px;
        }
        > div:not(.card) {
            width: 65%;
            height: 533px;
            flex-shrink: 0;
            img {
                width: 100%;
                height: 533px;
            }
        }
        .card {
        }
    }
    @media ${theme.device.laptop} {
        > div:not(.card) {
            width: 600px;
            height: 722px;
            flex-shrink: 0;
            img {
                width: 600px;
                height: 722px;
            }
        }
    }
`;

const doctors = [
    {
        name: 'Michael Gordon, DDS',
        position: 'Office Manager',
        bio: 'Dr. Michael J. Gordon, Jr is a North Carolina native. Dr. Gordon graduated high school in Fayetteville, NC then attended the United States Naval Academy where he earned an Aerospace Engineering Bachelor of Science degree while also completing his pre-dental courses. After fulfilling his service to the U. S. Navy, Dr. Gordon earned his Doctorate of Dental Surgery at the University of North Carolina in Chapel Hill.',
        image: () => (
            <StaticImage
                src="../images/staff/gordon.jpg"
                placeholder="blurred"
                quality={100}
                alt="Gordon"
            />
        ),
    },
    {
        name: 'Amanda',
        position: 'Registered Dental Hygienist',
        bio: 'Amanda received her Associate in Applied Science of Dental Hygiene degree from Catawba Valley Community College in 2016. She was awarded with the Preventive Dentistry Award for class of 2016. She has been with Claremont Family Dentistry since July 2016. She also recently received her licensure to be able to administer local anesthesia. Outside of work, she enjoys spending time with her husband, her 2 sons and her grandson. Some of her hobbies include hiking, CrossFit, and traveling. Amanda loves being a dental hygienist and taking care of her patients’ oral hygiene needs.',
        image: () => (
            <StaticImage
                src="../images/staff/amanda.jpg"
                placeholder="blurred"
                quality={100}
                alt="Amanda"
            />
        ),
    },
    {
        name: 'Kairah',
        position: 'Office Manager',
        bio: 'Kairah attended Wilkes Community College and before coming to our office in 2017 she worked as an Orthodontic Dental Assistant. She is our Office Manager and Lead Office Coordinator. She oversees the operations of the practice and works hard to make sure everything runs smoothly. She has a focused dedication to the practice, the patients and the employees and making sure every day at the office is successful. During her time away from work she enjoys shopping, the beach and spending time with her husband, Aaron and their 1 year old daughter.',
        image: () => (
            <StaticImage
                src="../images/staff/kairah.jpg"
                placeholder="blurred"
                quality={100}
                alt="Kairah"
            />
        ),
    },
    {
        name: 'Chelsei',
        position: 'Lead Dental Assistant',
        bio: 'Since graduating from Lake Norman Dental Assisting School in 2011, Chelsei has applied her talents to the dental field ever since. With working as a dental assistant for ten years now, she has the ability to make patients feel calm and at ease during all dental procedures. She also loves being apart of putting a smile on someone’s face daily. When she’s not working, Chelsei enjoys spending time with her sons Colt and Kash and their fur baby Pepper. She is a sports fanatic and loves to cheer on her North Carolina Tar Heels and Carolina Panthers!',
        image: () => (
            <StaticImage
                src="../images/staff/chelsei.jpg"
                placeholder="blurred"
                quality={100}
                alt="Chelsei"
            />
        ),
    },
    {
        name: 'Karalee',
        position: 'Dental Assistant',
        bio: 'Karalee is a graduate of Lake Norman Dental Assisting School which she completed in 2018, and accepted the position as a Dental Assistant here shortly after. She always strives to bring a smile to each patient no matter the situation. Being in the dental field had been a dream of hers since she was very young, wanting to follow in her mother’s footsteps. On Karalee’s days off, she enjoys spending time with her son and fur baby.',
        image: () => (
            <StaticImage
                src="../images/staff/karalee.jpg"
                placeholder="blurred"
                quality={100}
                alt="Karalee"
            />
        ),
    },
    {
        name: 'Yesenia',
        position: 'Registered Dental Hygienist (Hablo Español)',
        bio: 'Yesenia received her Associate in Science from Catawba Valley Community College in 2016. She then transferred to UNC-Chapel Hill where she received her Bachelor of Science degree in Dental Hygiene (Go Heels!!). Yesenia strives to make patients feel at ease at every appointment. In her spare time, she enjoys the outdoors with her fur baby, Bruno, and husband. She looks forward to meeting you!',
        image: () => (
            <StaticImage
                src="../images/staff/yasenia.jpg"
                placeholder="blurred"
                quality={100}
                alt="Yesenia"
            />
        ),
    },
    {
        name: 'Heather',
        position: 'Dental Assistant',
        bio: 'Since graduating from the Dental Assisting Program at WPCC in 2006, Heather has enjoyed her career in the dental field. She is passionate about helping patients understand their dental needs and treatment. Heather loves to see patients achieve a beautiful smile and restore their self confidence. Conover is her home, where she lives with her husband and two children. She enjoys the mountains and kayaking in the summer.',
        image: () => (
            <StaticImage
                src="../images/staff/heather.jpg"
                placeholder="blurred"
                quality={100}
                alt="Heather"
            />
        ),
    },
    {
        name: 'Lindsey',
        position: 'Office Coordinator',
        bio: 'Bio Coming Soon!',
        image: () => (
            <StaticImage
                src="../images/staff/lindsey.jpg"
                placeholder="blurred"
                quality={100}
                alt="Lindsey"
            />
        ),
    },
    {
        name: 'Katie',
        position: 'Hygienist',
        bio: 'Bio Coming soon!',
        image: () => (
            <StaticImage
                src="../images/staff/katie.jpg"
                placeholder="blurred"
                quality={100}
                alt="Katie"
            />
        ),
    },
];

export const OurTeam: React.FC = () => {
    const [index, setIndex] = useState<number>(0);

    return (
        <StyledOurTeam>
            <Section background="gray">
                <div className="container">
                    <Title position="center" className="title" border color="red">
                        Our Team
                    </Title>
                    <div className="outter-container">
                        <div className="selector-container">
                            {doctors.map((doctor, i) => {
                                return (
                                    <DoctorSelector
                                        index={index}
                                        i={i}
                                        key={i}
                                        onClick={() => {
                                            setIndex(i);
                                        }}
                                    >
                                        {doctor.image()}
                                        {
                                            <Icon
                                                name="triangle"
                                                className={`${i === index ? 'hide' : 'appear'}`}
                                            />
                                        }
                                    </DoctorSelector>
                                );
                            })}
                        </div>
                        <FeaturedDoc>
                            {doctors[index].image()}
                            <div className="card">
                                {' '}
                                <h2>{doctors[index].name}</h2>
                                <p>{doctors[index].position}</p>
                                <p>{doctors[index].bio}</p>
                            </div>
                        </FeaturedDoc>
                    </div>
                </div>
            </Section>
        </StyledOurTeam>
    );
};
